import { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useUserConfig } from '../hooks/useUserConfig';
import { usePagination } from '../hooks/usePagination';
import { useTableSort } from '../hooks/useTableSort';
import { useFetchPaginatedData } from '../hooks/useFetchPaginatedData';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { serializeParams } from '../utils/params';
import PaginationControls from '../components/PaginationControls';
import ErrorLoading from '../components/ErrorLoading';
import AutoRefreshControls from '../components/AutoRefreshControls';
import { formatDateTime } from '../utils/format';

const allHandicaps = [
  0, 0.25, -0.25, 0.5, -0.5, 0.75, -0.75, 1, -1, 1.25, -1.25, 1.5, -1.5, 1.75, -1.75, 2, -2,
];
const defaultHandicaps = [...allHandicaps];
const dataSource = 'sg_pools';

// One Click Filter 從「開賽前 5 分鐘」開始出現，之後一直保留（Freedom 8-30 規格）。
// 用意：那時盤口已定型，拿去比對歷史相似盤路才有意義；開賽後仍留著方便事後回看。
const KICKOFF_WINDOW_MS = 5 * 60 * 1000;
function isKickoffLinkVisible(startTime) {
  if (!startTime) return false;
  const diff = new Date(startTime).getTime() - Date.now();
  return diff <= KICKOFF_WINDOW_MS;
}

// 檢視模式（Freedom 9-06）：ah = 原本的亞盤檢視；x2 = 跳過亞盤、只看 1x2 並用賠率區間過濾。
// 兩種模式共用同一頁，只是換掉過濾條件與表格欄位。
const SIDE_OPTIONS = ['either', 'home', 'away', 'both'];

function SgPoolsData({ token, backendUrl, dateRange, setDateRange, error, setError, handleApplyDates }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const pagination = usePagination('sgPools.pageSize', 50);
  const { sortField, sortDirection, sortableHeader } = useTableSort('start_time', 'asc');
  const [homeHandicaps, setHomeHandicaps] = useUserConfig('sgPools.homeHandicap', defaultHandicaps);
  const [awayHandicaps, setAwayHandicaps] = useUserConfig('sgPools.awayHandicap', defaultHandicaps);

  const [mode, setMode] = useUserConfig('sgPools.mode', 'ah');
  const [minPrice, setMinPrice] = useUserConfig('sgPools.x2MinPrice', 1.75);
  const [maxPrice, setMaxPrice] = useUserConfig('sgPools.x2MaxPrice', 2.0);
  const [side, setSide] = useUserConfig('sgPools.x2Side', 'either');
  const [noAh, setNoAh] = useUserConfig('sgPools.x2NoAh', true);
  const isX2 = mode === 'x2';

  // 1x2 模式的已套用條件（改了輸入框但還沒按 Apply 時不會馬上打 API）
  const [x2Applied, setX2Applied] = useState({
    start_date: dateRange.start_date,
    end_date: dateRange.end_date,
    min_price: Number(minPrice),
    max_price: Number(maxPrice),
    side,
    no_ah: true,
  });
  const [x2Data, setX2Data] = useState([]);
  const [x2Scanned, setX2Scanned] = useState(0);
  const [x2Loading, setX2Loading] = useState(false);

  const params = useMemo(() => ({
    start_date: dateRange.start_date,
    end_date: dateRange.end_date,
    home_handicap: homeHandicaps,
    away_handicap: awayHandicaps,
  }), [dateRange.start_date, dateRange.end_date, homeHandicaps, awayHandicaps]);

  const { data, loading, refetch } = useFetchPaginatedData({
    dataUrl: `${backendUrl}/sg_pools/dates`,
    countUrl: `${backendUrl}/sg_pools/dates/count`,
    token,
    enabled: Boolean(!isX2 && token && dateRange.start_date && dateRange.end_date && !error),
    params,
    page: pagination.currentPage,
    pageSize: pagination.pageSize,
    setTotalCount: pagination.setTotalCount,
    setError,
  });

  const { setTotalCount } = pagination;
  const x2ParamsKey = JSON.stringify(x2Applied);

  // 1x2 模式：後端一次回全部命中場次，分頁在前端做
  const fetchX2 = useCallback(async (signal) => {
    if (!token) return;
    const applied = JSON.parse(x2ParamsKey);
    if (!applied.start_date || !applied.end_date) return;
    setX2Loading(true);
    try {
      const res = await axios.get(`${backendUrl}/sg_pools/dates/1x2`, {
        headers: { Authorization: `Bearer ${token}` },
        params: applied,
        signal,
      });
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      setX2Data(rows);
      setX2Scanned(res.data?.scanned || 0);
      setTotalCount(rows.length);
      setError(res.data?.error || null);
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('1x2 查詢錯誤:', err);
      setError(err.response?.data?.detail || err.message);
      setX2Data([]);
      setTotalCount(0);
    } finally {
      setX2Loading(false);
    }
  }, [token, backendUrl, x2ParamsKey, setTotalCount, setError]);

  useEffect(() => {
    if (!isX2) return undefined;
    const controller = new AbortController();
    fetchX2(controller.signal);
    return () => controller.abort();
  }, [isX2, fetchX2]);

  const autoRefresh = useAutoRefresh(
    (signal) => (isX2 ? fetchX2(signal) : refetch()),
    { defaultInterval: 60, defaultEnabled: true },
  );

  const handleHandicapChange = useCallback((handicap, isChecked, isHome) => {
    const setHandicaps = isHome ? setHomeHandicaps : setAwayHandicaps;
    setHandicaps((prev) => {
      const next = isChecked
        ? [...prev, handicap].filter((v, i, self) => self.indexOf(v) === i)
        : prev.filter((h) => h !== handicap);
      pagination.resetPage();
      return next;
    });
  }, [setHomeHandicaps, setAwayHandicaps, pagination]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    pagination.resetPage();
  }, [pagination]);

  const handleDateChange = useCallback((e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  }, [setDateRange]);

  const handleModeChange = useCallback((next) => {
    setMode(next);
    pagination.resetPage();
    setError(null);
  }, [setMode, pagination, setError]);

  const { filteredAndSortedData, pagedData } = useMemo(() => {
    const dataToFilter = isX2 ? x2Data : (Array.isArray(data) ? data : []);
    const normalizedQuery = searchQuery.toLowerCase().trim();

    const filteredData = normalizedQuery
      ? dataToFilter.filter((item) =>
          String(item.date_event_id).toLowerCase().includes(normalizedQuery) ||
          String(item.name).toLowerCase().includes(normalizedQuery)
        )
      : dataToFilter;

    const sorted = [...filteredData].sort((a, b) => {
      if (!sortField) return 0;
      const rawA = a[sortField];
      const rawB = b[sortField];
      if (typeof rawA === 'number' && typeof rawB === 'number') {
        return sortDirection === 'asc' ? rawA - rawB : rawB - rawA;
      }
      const aValue = rawA ? new Date(rawA).getTime() : 0;
      const bValue = rawB ? new Date(rawB).getTime() : 0;
      if (isNaN(aValue) || isNaN(bValue)) {
        const aStr = String(rawA ?? '').toLowerCase();
        const bStr = String(rawB ?? '').toLowerCase();
        if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    // 亞盤模式的分頁在後端做好了，1x2 模式一次拿全部、前端自己切
    if (!isX2) return { filteredAndSortedData: sorted, pagedData: sorted };
    const start = (pagination.currentPage - 1) * pagination.pageSize;
    return { filteredAndSortedData: sorted, pagedData: sorted.slice(start, start + pagination.pageSize) };
  }, [isX2, x2Data, data, searchQuery, sortField, sortDirection, pagination.currentPage, pagination.pageSize]);

  const handleApplyClick = useCallback(() => {
    pagination.resetPage();
    setError(null);
    if (isX2) {
      handleApplyDates();
      setX2Applied({
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        min_price: Number(minPrice),
        max_price: Number(maxPrice),
        side,
        no_ah: noAh,
      });
      return;
    }
    handleApplyDates(dataSource, homeHandicaps, awayHandicaps);
  }, [pagination, setError, handleApplyDates, isX2, dateRange, minPrice, maxPrice, side, noAh, homeHandicaps, awayHandicaps]);

  const downloadCSV = async () => {
    try {
      if (isX2) {
        const headers = [
          t('sgPools.eventId'), t('sgPools.name'), t('sgPools.startTime'),
          t('sgPools1x2.homeOdds'), t('sgPools1x2.drawOdds'), t('sgPools1x2.awayOdds'),
          t('sgPools.halfScore'), t('sgPools.fullScore'), t('sgPools1x2.oddsUpdatedAt'),
        ];
        const rows = filteredAndSortedData.map((item) => [
          item.date_event_id || '',
          item.name || '',
          formatDateTime(item.start_time),
          item.home_1x2_price ?? '',
          item.draw_1x2_price ?? '',
          item.away_1x2_price ?? '',
          item.half_score || '',
          item.full_score || '',
          formatDateTime(item.odds_updated_at),
        ]);
        const csvContent = [
          '﻿' + headers.join(','),
          ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${dataSource}_1x2_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      const response = await axios.get(`${backendUrl}/sg_pools/dates`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          skip: 0,
          limit: -1,
          home_handicap: homeHandicaps,
          away_handicap: awayHandicaps,
        },
        paramsSerializer: serializeParams,
      });
      const allEventData = Array.isArray(response.data.data) ? response.data.data : [];

      const normalizedQuery = searchQuery.toLowerCase().trim();
      const filteredData = normalizedQuery
        ? allEventData.filter((item) =>
            String(item.date_event_id).toLowerCase().includes(normalizedQuery) ||
            String(item.name).toLowerCase().includes(normalizedQuery)
          )
        : allEventData;

      const finalSortedData = [...filteredData].sort((a, b) => {
        if (!sortField) return 0;
        const aValue = a[sortField] ? new Date(a[sortField]).getTime() : 0;
        const bValue = b[sortField] ? new Date(b[sortField]).getTime() : 0;
        if (isNaN(aValue) || isNaN(bValue)) {
          const aStr = String(a[sortField] ?? '').toLowerCase();
          const bStr = String(b[sortField] ?? '').toLowerCase();
          if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
          if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });

      const headers = [
        t('sgPools.eventId'), t('sgPools.name'), t('sgPools.startTime'),
        t('sgPools.firstHomeHandicap'), t('sgPools.firstAwayHandicap'),
        t('sgPools.homeHandicap'), t('sgPools.awayHandicap'),
        t('sgPools.homeOdds'), t('sgPools.awayOdds'),
        t('sgPools.halfScore'), t('sgPools.fullScore'),
        t('common.createdAt'), t('common.updatedAt'),
      ];

      const rows = finalSortedData.map((item) => [
        item.date_event_id || '',
        item.name || '',
        formatDateTime(item.start_time),
        item.first_home_handicap || '',
        item.first_away_handicap || '',
        item.home_handicap || '',
        item.away_handicap || '',
        item.home_price || '',
        item.away_price || '',
        item.half_score || '',
        item.full_score || '',
        formatDateTime(item.created_at),
        formatDateTime(item.updated_at),
      ]);

      const csvContent = [
        '﻿' + headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${dataSource}_data_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('下載 CSV 錯誤:', err);
      setError(t('common.csvDownloadError'));
    }
  };

  const highlight = (inRange) => (inRange ? { fontWeight: 'bold', color: '#0d6efd' } : undefined);
  const isLoading = isX2 ? x2Loading : loading;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center my-4">
        <h2>{t('sgPools.title')}</h2>
        <div className="d-flex align-items-center gap-3">
          <AutoRefreshControls {...autoRefresh} />
          <button className="btn btn-success" onClick={downloadCSV}>
            {t('common.downloadCsv')}
          </button>
        </div>
      </div>

      {/* 檢視模式切換：亞盤（原本） / 1x2（跳過亞盤、用賠率區間過濾） */}
      <div className="btn-group mb-3" role="group">
        <button
          type="button"
          className={`btn btn-sm ${isX2 ? 'btn-outline-primary' : 'btn-primary'}`}
          onClick={() => handleModeChange('ah')}
        >
          {t('sgPools1x2.modeAh')}
        </button>
        <button
          type="button"
          className={`btn btn-sm ${isX2 ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => handleModeChange('x2')}
        >
          {t('sgPools1x2.modeX2')}
        </button>
      </div>

      {isX2 && <p className="text-muted small">{t('sgPools1x2.description')}</p>}

      <div className="mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-auto">
            <label htmlFor="search" className="form-label">{t('common.search')}</label>
            <input
              type="text"
              id="search"
              value={searchQuery}
              onChange={handleSearchChange}
              className="form-control"
              placeholder={t('sgPools.searchPlaceholder')}
            />
          </div>
          <div className="col-auto">
            <label htmlFor="start_date" className="form-label">{t('common.startDate')}</label>
            <input
              type="date"
              id="start_date"
              name="start_date"
              value={dateRange.start_date}
              onChange={handleDateChange}
              className="form-control"
            />
          </div>
          <div className="col-auto">
            <label htmlFor="end_date" className="form-label">{t('common.endDate')}</label>
            <input
              type="date"
              id="end_date"
              name="end_date"
              value={dateRange.end_date}
              onChange={handleDateChange}
              className="form-control"
            />
          </div>

          {isX2 && (
            <>
              <div className="col-auto">
                <label htmlFor="min_price" className="form-label">{t('sgPools1x2.minPrice')}</label>
                <input
                  type="number"
                  step="0.01"
                  id="min_price"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="form-control"
                  style={{ width: '110px' }}
                />
              </div>
              <div className="col-auto">
                <label htmlFor="max_price" className="form-label">{t('sgPools1x2.maxPrice')}</label>
                <input
                  type="number"
                  step="0.01"
                  id="max_price"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="form-control"
                  style={{ width: '110px' }}
                />
              </div>
              <div className="col-auto">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="no_ah"
                    checked={noAh}
                    onChange={(e) => setNoAh(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="no_ah">
                    {t('sgPools1x2.noAhOnly')}
                  </label>
                </div>
              </div>
              <div className="col-auto">
                <label htmlFor="side" className="form-label">{t('sgPools1x2.side')}</label>
                <select
                  id="side"
                  className="form-select"
                  value={side}
                  onChange={(e) => setSide(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  {SIDE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{t(`sgPools1x2.side_${s}`)}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="col-auto">
            <button className="btn btn-primary" onClick={handleApplyClick}>
              {t('common.apply')}
            </button>
          </div>
        </div>

        <p className="text-muted small mb-3 mt-2">
          {t('common.dateRange')}: {dateRange.start_date} {t('common.to')} {dateRange.end_date}
          {isX2
            ? <span> | {t('sgPools1x2.scanned')}: {x2Scanned} | {t('sgPools1x2.matched')}: {filteredAndSortedData.length}</span>
            : pagination.totalCount > 0 && <span> | {t('common.totalCount')}: {pagination.totalCount} | {t('common.filteredCount')}: {filteredAndSortedData.length}</span>}
        </p>

        {isX2 && x2Applied.side === 'both' && !x2Loading && filteredAndSortedData.length === 0 && (
          <div className="alert alert-warning py-2 small">{t('sgPools1x2.bothImpossible')}</div>
        )}

        {!isX2 && (
          <div className="mb-3">
            <div className="row g-2 align-items-start">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">{t('sgPools.homeHandicapFilter')}</div>
                  <div className="card-body">
                    <div className="d-flex flex-wrap gap-2">
                      {allHandicaps.map((h) => (
                        <div className="form-check form-check-inline" key={`home-${h}`}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`home_hcp_${h}`}
                            value={h}
                            checked={homeHandicaps.includes(h)}
                            onChange={(e) => handleHandicapChange(h, e.target.checked, true)}
                          />
                          <label className="form-check-label" htmlFor={`home_hcp_${h}`}>
                            {h > 0 ? `+${h}` : h}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">{t('sgPools.awayHandicapFilter')}</div>
                  <div className="card-body">
                    <div className="d-flex flex-wrap gap-2">
                      {allHandicaps.map((h) => (
                        <div className="form-check form-check-inline" key={`away-${h}`}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`away_hcp_${h}`}
                            value={h}
                            checked={awayHandicaps.includes(h)}
                            onChange={(e) => handleHandicapChange(h, e.target.checked, false)}
                          />
                          <label className="form-check-label" htmlFor={`away_hcp_${h}`}>
                            {h > 0 ? `+${h}` : h}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <PaginationControls {...pagination} />
      </div>

      <ErrorLoading error={error} loading={isLoading} />

      {pagedData && Array.isArray(pagedData) && !isLoading ? (
        <div className="table-responsive">
          <table className="table table-striped table-bordered table-hover">
            <thead>
              <tr>
                <th>{t('sgPools.eventId')}</th>
                {!isX2 && <th>{t('sgPools.oneClickFilter')}</th>}
                <th>{t('sgPools.name')}</th>
                {sortableHeader(t('sgPools.startTime'), 'start_time')}
                {isX2 ? (
                  <>
                    {sortableHeader(t('sgPools1x2.homeOdds'), 'home_1x2_price')}
                    {sortableHeader(t('sgPools1x2.drawOdds'), 'draw_1x2_price')}
                    {sortableHeader(t('sgPools1x2.awayOdds'), 'away_1x2_price')}
                  </>
                ) : (
                  <>
                    <th>{t('sgPools.firstHomeHandicap')}</th>
                    <th>{t('sgPools.firstAwayHandicap')}</th>
                    <th>{t('sgPools.homeHandicap')}</th>
                    <th>{t('sgPools.awayHandicap')}</th>
                    <th>{t('sgPools.homeOdds')}</th>
                    <th>{t('sgPools.awayOdds')}</th>
                  </>
                )}
                <th>{t('sgPools.halfScore')}</th>
                <th>{t('sgPools.fullScore')}</th>
                {isX2
                  ? sortableHeader(t('sgPools1x2.oddsUpdatedAt'), 'odds_updated_at')
                  : (
                    <>
                      {sortableHeader(t('common.createdAt'), 'created_at')}
                      {sortableHeader(t('common.updatedAt'), 'updated_at')}
                    </>
                  )}
              </tr>
            </thead>
            <tbody>
              {pagedData.map((item) => (
                <tr key={item.date_event_id}>
                  <td>
                    <Link to={`/events/${item.date_event_id}`} target="_blank">
                      {item.date_event_id}
                    </Link>
                  </td>
                  {!isX2 && (
                    <td>{isKickoffLinkVisible(item.start_time) ? (
                      <Link
                        to={`/sg-pools/similar/${item.date_event_id}`}
                        target="_blank"
                        className="btn btn-sm btn-outline-primary py-0"
                      >
                        {t('sgPools.oneClick')}
                      </Link>
                    ) : null}</td>
                  )}
                  <td><a href={item.url}>{item.name}</a></td>
                  <td>{formatDateTime(item.start_time)}</td>
                  {isX2 ? (
                    <>
                      <td style={highlight(item.home_in_range)}>{item.home_1x2_price}</td>
                      <td>{item.draw_1x2_price ?? '-'}</td>
                      <td style={highlight(item.away_in_range)}>{item.away_1x2_price}</td>
                    </>
                  ) : (
                    <>
                      <td>{item.first_home_handicap}</td>
                      <td>{item.first_away_handicap}</td>
                      <td>{item.home_handicap}</td>
                      <td>{item.away_handicap}</td>
                      <td>{item.home_price}</td>
                      <td>{item.away_price}</td>
                    </>
                  )}
                  <td>{item.half_score}</td>
                  <td>{item.full_score}</td>
                  {isX2 ? (
                    <td>{formatDateTime(item.odds_updated_at)}</td>
                  ) : (
                    <>
                      <td>{formatDateTime(item.created_at)}</td>
                      <td>{formatDateTime(item.updated_at)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !isLoading && <p className="text-muted">{t('common.noData')}</p>
      )}
    </div>
  );
}

export default SgPoolsData;
